import { Component, OnInit, OnDestroy, Inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidatorFn } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Observable, Subject, of, switchMap, forkJoin, throwError, EMPTY, interval, combineLatest } from 'rxjs';
import { RequestChangesDialogComponent } from '../components/request-changes-dialog/request-changes-dialog.component';
import { map, startWith, takeUntil, tap, catchError, finalize, takeWhile } from 'rxjs/operators';
import { ReleaseDefinition } from '../models/release-definition';
import { AdoService } from '../services/ado.service';
import { EnvironmentService } from '../services/environment.service';
import { LoadingService } from '../services/loading.service';
import { UserProfileService } from '../services/user-profile.service';
import { PlatformPortalApiService } from '../services/platform-portal-api.service';
import { AdoUserProfile } from '../models/ado-user-profile';
import { AdoAccount } from '../models/ado-account';
import { Environment } from '../models/environment';
import { YamlConversionResultFile } from '../models/yaml-conversion-result-file';
import { CreateYamlConversionPrRequest } from '../models/create-yaml-conversion-pr-request';
import { FileNode } from '../models/file-node';
import { YamlConversionRequest } from '../models/yaml-conversion-request';
import { YamlConversionStatusResponse } from '../models/yaml-conversion-status-response';
import { RefineYamlConversionRequest } from '../models/refine-yaml-conversion-request';
import { OrchestrationRuntimeStatus } from '../models/orchestration-runtime-status';
import { GetYamlConversionStatusRequest } from '../models/get-yaml-conversion-status-request';
import { ConversionStep, ConversionStepState, CONVERSION_STEP_DEFINITIONS, INITIAL_CONVERSION_FLOW, REFINEMENT_CONVERSION_FLOW, SERVER_STATUS_MAP } from '../models/conversion-steps';

@Component({
  selector: 'app-classic-release-migration',
  standalone: false,
  templateUrl: './classic-release-migration.component.html',
  styleUrls: ['./classic-release-migration.component.css']
})
export class ClassicReleaseMigrationComponent implements OnInit, OnDestroy {
  
  // ===== 1. CONFIGURATION PROPERTIES =====
  private readonly environment: Environment;

  // ===== 2. FORM PROPERTIES =====
  migrationForm: FormGroup;

  // ===== 3. DATA PROPERTIES =====
  releaseDefinitions: Array<ReleaseDefinition> = [];
  rcReleaseDefinitions: Array<ReleaseDefinition> = [];
  devReleaseDefinitions: Array<ReleaseDefinition> = [];
  private availableAdoAccounts: Array<AdoAccount> = [];
  private defaultOrg?: AdoAccount;
  private defaultProject?: string;

  // ===== 4. OBSERVABLE PROPERTIES =====
  filteredReleaseDefinitions: Observable<Array<ReleaseDefinition>>;
  filteredDevReleaseDefinitions: Observable<Array<ReleaseDefinition>>;

  // ===== 5. STATE MANAGEMENT PROPERTIES =====
  conversionState: 'form' | 'in_progress' | 'complete' | 'pr_complete' | 'failed' = 'form';
  conversionError: string | null = null;
  private isConverting = false;

  // ===== 6. CONVERSION WORKFLOW PROPERTIES =====
  conversionSteps: ConversionStepState[] = [];
  conversionResult: YamlConversionResultFile[] = [];
  pullRequestUrl: string | null = null;
  private statusQueryUri: string | null = null;
  private instanceId: string | null = null;
  private iteration: number = 1;

  // ===== 7. FILE MANAGEMENT PROPERTIES =====
  treeDataSource: FileNode[] = [];
  selectedFile: FileNode | null = null;
  selectedFileNotes: string | null = null;

  // ===== 8. INTERNAL/PRIVATE PROPERTIES =====
  showRequestChangesButton = true;
  private readonly _destroying$ = new Subject<void>();
  private lastUserPrompt: string | null = null;
  lastFailedOperation: 'conversion' | 'refinement' | 'loadResults' | 'createPR' | null = null;

  // ===== 1. ANGULAR LIFECYCLE METHODS =====

  constructor(
    @Inject(FormBuilder) private readonly formBuilder: FormBuilder,
    @Inject(AdoService) private readonly adoService: AdoService,
    @Inject(EnvironmentService) private readonly environmentService: EnvironmentService,
    @Inject(LoadingService) public readonly loadingService: LoadingService,
    @Inject(UserProfileService) private readonly userProfileService: UserProfileService,
    @Inject(PlatformPortalApiService) private readonly platformPortalApiService: PlatformPortalApiService,
    private readonly dialog: MatDialog
  ) {
    this.environment = this.environmentService.getEnvironment();
    
    this.migrationForm = this.formBuilder.group({
      rcReleaseDefinition: ['', [Validators.required, this.rcReleaseDefinitionValidator()]],
      devEnabled: [false],
      devReleaseDefinition: ['']
    });

    this.filteredReleaseDefinitions = of(this.releaseDefinitions);
    this.filteredDevReleaseDefinitions = of(this.releaseDefinitions);

    this.setupFormWatchers();
  }

  /**
   * Angular lifecycle hook called after component initialization.
   * Checks user authentication status and initializes the component if authenticated.
   * Sets up the complete initialization sequence including user profile, accounts, and release definitions.
   */
  ngOnInit(): void {
    this.userProfileService.isAuthenticated()
      .pipe(takeUntil(this._destroying$))
      .subscribe({
        next: (isAuthenticated) => {
          if (isAuthenticated) {
            this.initializeComponent()
              .pipe(
                takeUntil(this._destroying$)
              )
              .subscribe({
                next: () => {
                  console.log('Component initialization completed successfully');
                },
                error: (error) => {
                  console.error('Error in initialization sequence:', error);
                }
              });
          }
        },
        error: (error) => {
          console.error('Authentication service error:', error);
        }
      });
  }

  /**
   * Angular lifecycle hook called when component is destroyed.
   * Properly cleans up all active subscriptions to prevent memory leaks.
   */
  ngOnDestroy(): void {
    this._destroying$.next();
    this._destroying$.complete();
  }

  // ===== 2. INITIALIZATION METHODS =====

  /**
   * Initializes the application by loading all required data in sequence
   * @returns Observable that completes when all initialization steps are done
   */
  private initializeComponent(): Observable<{ value: any[] }> {
    this.loadingService.show('classic-release-migration');
    return this.loadUserProfile().pipe(
      switchMap((profile) => this.loadUserAccounts(profile.publicAlias)),
      switchMap(() => this.loadProjectsForAccounts()),
      tap(() => this.setDefaultOrg()),
      tap(() => this.setDefaultProject()),
      switchMap(() => this.loadReleaseDefinitions()),
      finalize(() => this.loadingService.hide('classic-release-migration'))
    );
  }

  /**
   * Loads the current user profile
   * @returns Observable of the user profile
   */
  private loadUserProfile(): Observable<AdoUserProfile> {
    return this.userProfileService.getCurrentProfile().pipe(
      map((profile) => {
        if (!profile) {
          throw new Error('User profile not found');
        }
        return profile;
      })
    );
  }

  /**
   * Loads user accounts based on the public alias
   * @param publicAlias The user's public alias
   * @returns Observable of the accounts response
   */
  private loadUserAccounts(publicAlias: string): Observable<{ value: AdoAccount[] }> {
    return this.userProfileService.getAccounts(publicAlias).pipe(
      tap(accounts => {
        console.log(`Successfully loaded ${accounts.value.length} user accounts`);
        this.availableAdoAccounts = accounts.value;
      }),
      catchError(error => {
        console.error('Error loading user accounts:', error);
        return throwError(() => new Error('Failed to load user accounts'));
      })
    );
  }

  /**
   * Loads projects for all available accounts
   * @returns Observable that completes when all projects are loaded
   */
  private loadProjectsForAccounts(): Observable<{ value: any[] }[]> {
    if (this.availableAdoAccounts.length === 0) {
      return of([] as { value: any[] }[]);
    }

    const projectObservables = this.availableAdoAccounts.map(account =>
      this.userProfileService.getProjects(account.accountName).pipe(
        tap(projects => {
          account.projects = account.projects || [];
          projects.value.forEach((project: any) => {
            account.projects.push(project.name);
          });

          if (this.defaultOrg?.accountName === account.accountName) {
            this.defaultOrg.projects = account.projects;
          }
        }),
        catchError(error => {
          console.error(`Error fetching projects for account ${account.accountName}:`, error);
          return of({ value: [] });
        })
      )
    );

    return forkJoin(projectObservables).pipe(
      catchError(error => {
        console.error('Error in loadProjectsForAccounts:', error);
        return throwError(() => new Error('Failed to fetch projects for accounts'));
      })
    );
  }

  /**
   * Sets the default organization account from available accounts based on environment configuration.
   * Finds the account matching the configured default organization name (case-insensitive).
   */
  private setDefaultOrg(): void {
    this.defaultOrg = this.availableAdoAccounts.find(
      x => x.accountName.toLowerCase() === this.environment.defaultOrg.toLowerCase()
    );
  }

  /**
   * Sets the default project name from the default organization's projects.
   * Matches against the configured default project name (case-insensitive).
   * Resets to undefined if default org has no projects loaded.
   */
  private setDefaultProject(): void {
    if (!this.defaultOrg?.projects) {
      this.defaultProject = undefined;
      return;
    }

    const defaultProject = this.defaultOrg.projects.find(
      x => x.toLowerCase() === this.environment.defaultProject.toLowerCase()
    );

    this.defaultProject = defaultProject;
  }

  /**
   * Loads all release definitions from Azure DevOps for the default organization and project.
   * Clears existing data, fetches from ADO service, processes results, and sets up autocomplete.
   * @returns Observable that emits the release definitions response or empty array on error
   */
  loadReleaseDefinitions(): Observable<{ value: any[] }> {
    if (!this.defaultOrg || !this.defaultProject) {
      console.warn('Default org or project not available for loading release definitions');
      return of({ value: [] });
    }

    this.releaseDefinitions = [];
    this.rcReleaseDefinitions = [];
    this.devReleaseDefinitions = [];

    return this.adoService.GetAllReleaseDefinitions(this.defaultOrg.accountName, this.defaultProject).pipe(
      tap((result: any) => {
        this.processReleaseDefinitions(result);
        this.setupAutocompleteObservables();
      }),
      catchError(error => {
        console.error('Error loading release definitions:', error);
        return of({ value: [] });
      })
    );
  }

  /**
   * Processes raw release definitions from Azure DevOps API response.
   * Categorizes pipelines into RC (-rc suffix) and DEV (-dev suffix) arrays.
   * Sorts all arrays alphabetically by pipeline name for consistent UI display.
   * @param result Raw response from Azure DevOps GetAllReleaseDefinitions API
   */
  processReleaseDefinitions(result: any): void {
    for (const def of result.value) {
      const release = { name: def.name, id: def.id };
      this.releaseDefinitions.push(release);
      
      if (def.name.toLowerCase().endsWith('-rc')) {
        this.rcReleaseDefinitions.push(release);
      } else if (def.name.toLowerCase().endsWith('-dev')) {
        this.devReleaseDefinitions.push(release);
      }
    }

    this.releaseDefinitions.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
    this.rcReleaseDefinitions.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
    this.devReleaseDefinitions.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
  }


  // ===== 3. FORM AND VALIDATION METHODS =====

  /**
   * Sets up autocomplete observables for both RC and dev release definition form controls.
   * Creates filtered streams that respond to user input and show matching pipeline names.
   * Handles both string values (user typing) and ReleaseDefinition objects (selected items).
   */
  setupAutocompleteObservables(): void {
    this.filteredReleaseDefinitions = this.migrationForm.get('rcReleaseDefinition')!.valueChanges.pipe(
      startWith<string | ReleaseDefinition>(''),
      map((value) => typeof value === 'string' ? value : value?.name ?? ''),
      map((value) => value === '' ? this.rcReleaseDefinitions : this.filterRcReleaseDefinitions(value))
    );

    this.filteredDevReleaseDefinitions = this.migrationForm.get('devReleaseDefinition')!.valueChanges.pipe(
      startWith<string | ReleaseDefinition>(''),
      map((value) => typeof value === 'string' ? value : value?.name ?? ''),
      map((value) => value === '' ? this.devReleaseDefinitions : this.filterDevReleaseDefinitions(value))
    );
  }

  /**
   * Sets up reactive form watchers for dynamic validation behavior.
   * Uses combineLatest to efficiently watch multiple form controls in a single subscription.
   * Handles dev checkbox validation and RC release definition changes to revalidate dev field.
   * Uses takeUntil pattern to properly manage subscription lifecycle.
   */
  setupFormWatchers(): void {
    const devEnabled$ = this.migrationForm.get('devEnabled')?.valueChanges.pipe(startWith(false)) ?? of(false);
    const rcReleaseDefinition$ = this.migrationForm.get('rcReleaseDefinition')?.valueChanges.pipe(startWith('')) ?? of('');
    
    combineLatest([devEnabled$, rcReleaseDefinition$])
      .pipe(takeUntil(this._destroying$))
      .subscribe(([devEnabled, rcValue]) => {
        this.updateDevValidation(devEnabled);
        this.revalidateDevFieldIfNeeded(devEnabled, rcValue);
      });
  }

  /**
   * Updates validation rules for the dev release definition field based on dev checkbox state.
   * @param enabled Whether the dev checkbox is enabled
   */
  private updateDevValidation(enabled: boolean): void {
    const devControl = this.migrationForm.get('devReleaseDefinition');
    if (enabled) {
      devControl?.setValidators([Validators.required, this.devReleaseDefinitionValidator()]);
    } else {
      devControl?.clearValidators();
      devControl?.setValue('');
    }
    devControl?.updateValueAndValidity();
  }

  /**
   * Conditionally revalidates the dev field when RC release definition changes.
   * @param devEnabled Whether the dev checkbox is enabled
   * @param rcValue The current RC release definition value
   */
  private revalidateDevFieldIfNeeded(devEnabled: boolean, rcValue: any): void {
    const devControl = this.migrationForm.get('devReleaseDefinition');
    if (devEnabled && devControl?.value && rcValue) {
      devControl.updateValueAndValidity();
    }
  }

  /**
   * Filters release definitions based on user input text.
   * Performs case-insensitive substring matching against pipeline names.
   * @param val User input text to filter by
   * @param releaseDefinitions Array of release definitions to filter
   * @returns Filtered array of matching release definitions
   */
  filterReleaseDefinitions(val: string, releaseDefinitions: Array<ReleaseDefinition>): Array<ReleaseDefinition> {
    const filterValue = val.toLowerCase();
    return releaseDefinitions.filter(option =>
      option.name.toLowerCase().includes(filterValue)
    );
  }

  /**
   * Filters RC release definitions for autocomplete functionality.
   * @param val User input text to filter by
   * @returns Filtered array of matching RC release definitions
   */
  filterRcReleaseDefinitions(val: string): Array<ReleaseDefinition> {
    return this.filterReleaseDefinitions(val, this.rcReleaseDefinitions);
  }

  /**
   * Filters dev release definitions for autocomplete functionality.
   * @param val User input text to filter by
   * @returns Filtered array of matching dev release definitions
   */
  filterDevReleaseDefinitions(val: string): Array<ReleaseDefinition> {
    return this.filterReleaseDefinitions(val, this.devReleaseDefinitions);
  }

  /**
   * Display function for Material autocomplete to show release definition names.
   * @param release Optional release definition object
   * @returns Release name or empty string if no release provided
   */
  displayReleaseName(release?: ReleaseDefinition): string {
    return release ? release.name : '';
  }

  /**
   * Creates a validator function for release definition form controls.
   * Validates that the input matches an existing release definition and converts string to object.
   * @param releaseDefinitions Array of valid release definitions to validate against
   * @returns ValidatorFn that validates and transforms the control value
   */
  createReleaseDefinitionValidator(releaseDefinitions: Array<ReleaseDefinition>): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (typeof control.value === 'string' && control.value) {
        if (!releaseDefinitions.some(r => r.name === control.value)) {
          return { 'invalidReleaseDefinition': { value: control.value } };
        }
        control.setValue(releaseDefinitions.find(r => r.name === control.value));
      }
      return null;
    };
  }

  /**
   * Validator function for RC release definition form control.
   * @returns ValidatorFn configured for RC release definitions
   */
  rcReleaseDefinitionValidator(): ValidatorFn {
    return this.createReleaseDefinitionValidator(this.rcReleaseDefinitions);
  }

  /**
   * Validator function for dev release definition form control.
   * @returns ValidatorFn configured for dev release definitions
   */
  devReleaseDefinitionValidator(): ValidatorFn {
    return this.createReleaseDefinitionValidator(this.devReleaseDefinitions);
  }

  /**
   * Validates whether the conversion process can be initiated.
   * Checks form validation state, dev pipeline requirements, and ensures no conversion is currently running.
   * Used to enable/disable the Convert to YAML button and validate before starting conversion.
   * @returns True if conversion can proceed, false otherwise
   */
  canConvert(): boolean {
    const rcReleaseDefinitionValid = this.migrationForm.get('rcReleaseDefinition')?.valid;
    const devEnabled = this.migrationForm.get('devEnabled')?.value;
    const devValid = !devEnabled || this.migrationForm.get('devReleaseDefinition')?.valid;
    
    return !!(rcReleaseDefinitionValid && devValid && !this.isConverting);
  }

  /**
   * Determines if the current operation is a refinement (vs initial conversion).
   * @returns True if this is a refinement operation, false if initial conversion
   */
  isRefinement(): boolean {
    return this.iteration > 1 || !!this.lastUserPrompt;
  }

  // ===== 4. CONVERSION WORKFLOW METHODS =====

  /**
   * Creates conversion step states from a step flow array.
   * @param flow Array of conversion steps to create states for
   * @returns Array of ConversionStepState with all steps set to pending
   */
  private createStepsFromFlow(flow: ConversionStep[]): ConversionStepState[] {
    return flow.map(stepKey => ({
      ...CONVERSION_STEP_DEFINITIONS[stepKey],
      status: 'Pending' as const,
      icon: CONVERSION_STEP_DEFINITIONS[stepKey].defaultIcon
    }));
  }

  /**
   * Finds the index of a step in the current conversion steps array.
   * @param stepKey The step to find
   * @returns Index of the step, or -1 if not found
   */
  private findStepIndex(stepKey: ConversionStep): number {
    return this.conversionSteps.findIndex(step => step.key === stepKey);
  }

  /**
   * Initiates the YAML conversion process for selected release pipelines.
   * This is the main entry point for both initial conversions and refinement iterations.
   * Validates form state before proceeding and determines whether to start initial or refinement conversion.
   * @param userPrompt Optional user prompt for refinement iterations. If provided, triggers refinement conversion.
   */
  convertToYaml(userPrompt?: string): void {
    if (!this.canConvert() && !userPrompt) {
      console.log('Conversion blocked - validation failed');
      return;
    }

    const rcRelease = this.migrationForm.get('rcReleaseDefinition')?.value;
    const devEnabled = this.migrationForm.get('devEnabled')?.value;
    const devRelease = this.migrationForm.get('devReleaseDefinition')?.value;
    
    console.log('Starting YAML conversion', JSON.stringify({ 
      type: userPrompt ? 'refinement' : 'initial',
      rcRelease: rcRelease?.name,
      devEnabled,
      devRelease: devRelease?.name,
      iteration: userPrompt ? this.iteration + 1 : 1
    }));

    this.isConverting = true;
    const previousState = this.conversionState;
    this.conversionState = 'in_progress';
    console.log(`State transition: ${previousState} → in_progress`, 'Starting conversion');
    this.conversionError = null;

    if (userPrompt) {
      this.startRefinementConversion(userPrompt);
    } else {
      this.startInitialConversion();
    }
  }

  /**
   * Starts the initial YAML conversion process.
   * Resets iteration counter, creates initial conversion request, initializes step progress UI,
   * and begins the conversion workflow by calling the platform API service.
   * Sets up polling for status updates after successful conversion start.
   */
  startInitialConversion(): void {
    this.iteration = 1;
    this.lastUserPrompt = null;
    const request = this.createInitialConversionRequest();
    
    this.conversionSteps = this.createStepsFromFlow(INITIAL_CONVERSION_FLOW);
    
    if (this.conversionSteps.length > 0) {
      this.conversionSteps[0].status = 'In Progress';
      this.conversionSteps[0].icon = 'autorenew';
    }
    
    this.platformPortalApiService.startConversion(request)
      .pipe(
        takeUntil(this._destroying$),
        catchError(error => {
          this.handleConversionError(error, 'conversion');
          return EMPTY;
        })
      )
      .subscribe(response => {
        console.log('Conversion started successfully:', JSON.stringify(response, null, 2));
        this.statusQueryUri = response.StatusQueryGetUri;
        this.instanceId = response.Id;
        console.log('Conversion started with instance ID:', this.instanceId);
        this.pollForStatus(10000);
      });
  }

  /**
   * Starts a refinement conversion using user feedback.
   * Increments iteration counter, creates refinement request with user prompt,
   * initializes refinement step progress UI, and begins the refinement workflow.
   * @param userPrompt User feedback text for refining the previous conversion
   */
  startRefinementConversion(userPrompt: string): void {
    this.lastUserPrompt = userPrompt;
    const refineRequest = this.createRefinementRequest(userPrompt);
  
    this.conversionSteps = this.createStepsFromFlow(REFINEMENT_CONVERSION_FLOW);
    
    if (this.conversionSteps.length > 0) {
      this.conversionSteps[0].status = 'In Progress';
      this.conversionSteps[0].icon = 'autorenew';
    }
    
    this.platformPortalApiService.refineConversion(refineRequest)
      .pipe(
        takeUntil(this._destroying$),
        catchError(error => {
          this.handleConversionError(error, 'refinement');
          return EMPTY;
        })
      )
      .subscribe(response => {
        console.log('Refinement started successfully:', JSON.stringify(response, null, 2));
        this.iteration++;
        this.statusQueryUri = response.StatusQueryGetUri;
        console.log('Refinement started with instance ID:', response.Id);
        this.pollForStatus(7000);
      });
  }

  /**
   * Creates the request object for initial YAML conversion.
   * Extracts form values for RC and optional dev pipelines and builds the API request.
   * @returns YamlConversionRequest with project name and pipeline IDs
   */
  createInitialConversionRequest(): YamlConversionRequest {
    const rcReleaseDefinition = this.migrationForm.get('rcReleaseDefinition')?.value as ReleaseDefinition;
    const devEnabled = this.migrationForm.get('devEnabled')?.value;
    const devReleaseDefinition = devEnabled
      ? this.migrationForm.get('devReleaseDefinition')?.value as ReleaseDefinition
      : null;

    return {
      projectName: this.defaultProject!,
      classicReleaseDefinitionId: rcReleaseDefinition.id,
      classicDevReleaseDefinitionId: devEnabled ? devReleaseDefinition!.id : null
    };
  }

  /**
   * Creates the request object for refinement conversion.
   * Uses the current instance ID, user prompt, and iteration number for refinement.
   * @param userPrompt User feedback text for refining the conversion
   * @returns RefineYamlConversionRequest with refinement parameters
   */
  createRefinementRequest(userPrompt: string): RefineYamlConversionRequest {
    return {
      instanceId: this.instanceId!,
      userPrompt: userPrompt,
      currentIterationNumber: this.iteration,
    };
  }


  /**
   * Continuously polls the Azure Durable Functions orchestration status until completion.
   * Uses a timer-based approach at configurable intervals to check conversion progress.
   * Automatically stops polling when orchestration reaches terminal state (Completed/Failed).
   * Uses takeWhile to continue polling while status is Running or Pending, includes final emit.
   * Implements proper error handling and subscription cleanup via takeUntil pattern.
   */
  pollForStatus(durationMs: number): void {
    console.log('Starting status polling...');
    interval(durationMs)
      .pipe(
        switchMap(() => {
          const request: GetYamlConversionStatusRequest = {
            statusUrl: this.statusQueryUri!
          };
          console.log('Polling for status with request:', JSON.stringify(request));
          return this.platformPortalApiService.getConversionStatus(request);
        }),
        tap(statusResponse => {
          this.updateConversionProgress(statusResponse);
        }),
        takeUntil(this._destroying$),
        takeWhile(statusResponse => statusResponse.runtimeStatus === OrchestrationRuntimeStatus.Running || statusResponse.runtimeStatus === OrchestrationRuntimeStatus.Pending, true),
        catchError(error => {
          this.handleConversionError(error);
          return EMPTY;
        }),
        finalize(() => {
          this.isConverting = false;
        })
      )
      .subscribe();
  }


  /**
   * Updates the UI conversion progress based on orchestration status response.
   * Handles different orchestration states: Failed, Completed, Running/Pending.
   * Routes to appropriate handlers for terminal states or updates progress for running states.
   * Called by the status polling mechanism to provide real-time user feedback.
   * @param statusResponse The status response from Azure Durable Functions orchestration
   */
  updateConversionProgress(statusResponse: YamlConversionStatusResponse): void {    
    if (statusResponse.runtimeStatus === OrchestrationRuntimeStatus.Failed) {
      this.handleFailedConversion(statusResponse);
      return;
    }

    if (statusResponse.runtimeStatus === OrchestrationRuntimeStatus.Completed) {
      this.handleCompletedConversion();
      return;
    }

    this.updateRunningStepProgress(statusResponse);
  }

  /**
   * Updates the progress UI for running conversion steps based on server status.
   * Maps server custom status to conversion steps and updates the active step progress.
   * Handles null/undefined status gracefully and warns about unknown server statuses.
   * @param statusResponse The status response containing custom status from orchestration
   */
  updateRunningStepProgress(statusResponse: YamlConversionStatusResponse): void {
    if (!statusResponse.customStatus) {
      console.log('Progress update: customStatus is null/undefined, remaining at current step');
      return;
    }

    const serverStep = SERVER_STATUS_MAP[statusResponse.customStatus];
    if (!serverStep) {
      console.warn(`Unknown server status: ${statusResponse.customStatus}`);
      return;
    }
    
    const currentStatusIndex = this.findStepIndex(serverStep);
    console.log(`Progress update: currentStatusIndex=${currentStatusIndex}, customStatus=${statusResponse.customStatus}, mappedStep=${serverStep}`);

    if (currentStatusIndex > -1) {
      this.updateStepStatusesAtIndex(currentStatusIndex);
    }
  }

  /**
   * Updates the visual status of all conversion steps based on the current active step index.
   * Sets steps before current index to 'Complete', current index to 'In Progress', and remaining to 'Pending'.
   * Updates both status text and icon for each step to provide clear visual feedback.
   * @param currentStatusIndex The index of the currently active conversion step
   */
  updateStepStatusesAtIndex(currentStatusIndex: number): void {
    for (let i = 0; i < this.conversionSteps.length; i++) {
      if (i < currentStatusIndex) {
        this.conversionSteps[i].status = 'Complete';
        this.conversionSteps[i].icon = 'check_circle';
      } else if (i === currentStatusIndex) {
        this.conversionSteps[i].status = 'In Progress';
        this.conversionSteps[i].icon = 'autorenew';
      } else {
        this.conversionSteps[i].status = 'Pending';
        this.conversionSteps[i].icon = 'pending';
      }
    }
  }

  /**
   * Handles successful completion of the YAML conversion orchestration.
   * Marks steps up to "Completed" as done, then starts "Retrieving file content" step.
   * Loads conversion results from the backend API.
   * Called when the orchestration status is OrchestrationRuntimeStatus.Completed.
   * @param statusResponse The completed status response containing conversion output data
   */
  handleCompletedConversion(): void {
    console.log('Conversion completed! Starting file content retrieval step');
    
    const completedIndex = this.findStepIndex(ConversionStep.COMPLETED);
    for (let i = 0; i <= completedIndex; i++) {
      if (i < this.conversionSteps.length) {
        this.conversionSteps[i].status = 'Complete';
        this.conversionSteps[i].icon = 'check_circle';
      }
    }
    
    const fileRetrievalIndex = this.findStepIndex(ConversionStep.RETRIEVING_FILES);
    if (fileRetrievalIndex > -1) {
      this.conversionSteps[fileRetrievalIndex].status = 'In Progress';
      this.conversionSteps[fileRetrievalIndex].icon = 'autorenew';
    }
    
    this.loadConversionResults();
  }

  /**
   * Schedules the transition to the results view after conversion completion.
   * Marks the file retrieval step as complete, validates that files were generated,
   * and transitions to either the complete state (with results) or failed state (no files).
   * Builds the file tree for display and clears any previous file selections.
   * @param output Array of generated YAML files from the conversion process
   */
  scheduleResultsTransition(output: YamlConversionResultFile[]): void {
      console.log('File content retrieved! Processing results...');
      
      const fileRetrievalIndex = this.findStepIndex(ConversionStep.RETRIEVING_FILES);
      if (fileRetrievalIndex > -1) {
        this.conversionSteps[fileRetrievalIndex].status = 'Complete';
        this.conversionSteps[fileRetrievalIndex].icon = 'check_circle';
      }
      
      if (output.length === 0) {
        console.log('No files returned from conversion - showing error state');
        this.conversionState = 'failed';
        this.isConverting = false;
        this.conversionError = 'No files were generated during the conversion process. This may indicate an issue with the source pipeline configuration or the conversion logic. Please try again or request changes with more specific requirements.';
        this.lastFailedOperation = null;
        return;
      }
      
      console.log('Files returned successfully - transitioning to results page');
      this.conversionState = 'complete';
      this.conversionResult = output;
      this.treeDataSource = this.buildFileTree(this.conversionResult);
      this.selectedFile = null;
      this.selectedFileNotes = null;
  }

  /**
   * Loads the conversion results from the backend API after successful completion.
   * Uses the current instance ID and iteration number to fetch the specific conversion results.
   */
  loadConversionResults(): void {
    this.platformPortalApiService.getConversionResult(this.instanceId!, this.iteration)
      .pipe(
        takeUntil(this._destroying$),
        catchError(error => {
          this.handleConversionError(error, 'loadResults');
          return EMPTY;
        })
      )
      .subscribe(conversionResult => {
        this.scheduleResultsTransition(conversionResult);
      });
  }

  /**
   * Handles failed conversion orchestration by setting error state and displaying failure message.
   * Sets the conversion state to failed, stops the converting flag, and displays the error output.
   * Clears the status query URI to stop any further polling attempts.
   * @param statusResponse The failed status response containing error details
   */
  handleFailedConversion(statusResponse: YamlConversionStatusResponse): void {
    this.conversionState = 'failed';
    this.isConverting = false;
    this.conversionError = statusResponse.output ?? 'The conversion process failed';
    this.statusQueryUri = null;
  }

  /**
   * Handles conversion errors by showing failed state and user feedback
   * @param error The error that occurred
   * @param operation The operation that failed (for smart retry functionality)
   */
  handleConversionError(error: any, operation?: 'conversion' | 'refinement' | 'loadResults' | 'createPR'): void {
    console.error('Conversion error:', error);
    
    this.conversionState = 'failed';
    this.isConverting = false;
    this.conversionError = error?.message ?? 'An unexpected error occurred';
    this.lastFailedOperation = operation ?? null;
    
    this.statusQueryUri = null;
  }

  /**
   * Retries the last failed operation based on the failure context
   * Provides intelligent retry functionality that doesn't restart the entire workflow
   */
  retryLastOperation(): void {
    console.log('Retrying last failed operation:', this.lastFailedOperation);
    
    switch (this.lastFailedOperation) {
      case 'conversion':
        this.convertToYaml();
        break;
      case 'refinement':
        this.convertToYaml(this.lastUserPrompt!);
        break;
      case 'loadResults':
        this.loadConversionResults();
        break;
      case 'createPR':
        this.createPullRequest();
        break;
      default:
        console.warn('No specific failed operation found, falling back to full conversion');
        this.convertToYaml();
    }
  }

  // ===== 5. PULL REQUEST METHODS =====

  /**
   * Initiates the pull request creation process for the converted YAML files.
   * Creates a pull request containing the generated YAML pipeline files from the conversion.
   * Shows loading spinner during the operation and uses the real API implementation.
   * Uses the current instance ID and iteration number to identify the conversion results to include.
   */
  createPullRequest(): void {
    this.loadingService.show('classic-release-migration');
    
    const request: CreateYamlConversionPrRequest = {
      instanceId: this.instanceId!,
      iterationNumber: this.iteration,
      workItemId: null
    };

    console.log('Creating pull request with details:', request);

    this.platformPortalApiService.createPullRequest(request)
      .pipe(
        takeUntil(this._destroying$),
        catchError(error => {
          this.handleConversionError(error, 'createPR');
          return EMPTY;
        }),
        finalize(() => {
          this.loadingService.hide('classic-release-migration');
        })
      )
      .subscribe(response => {
        this.pullRequestUrl = response.PullRequestUrl;
        this.conversionState = 'pr_complete';
      });
  }

  // ===== 6. FILE MANAGEMENT METHODS =====

  /**
   * Constructs a hierarchical file tree structure from conversion result files.
   * Transforms flat file paths into a nested tree structure suitable for Material Tree display.
   * Creates folder nodes for directory paths and file nodes for actual files with content.
   * Handles path normalization by removing './' prefixes and filtering empty path segments.
   * Associates file content and notes with file nodes for preview functionality.
   * @param files Array of YamlConversionResultFile objects containing file paths, content, and notes
   * @returns Array of FileNode objects representing the root-level tree structure
   */
  buildFileTree(files: YamlConversionResultFile[]): FileNode[] {
    const root: FileNode = { name: 'root', type: 'folder', children: [] };

    for (const file of files) {
      this.processFileIntoTree(file, root);
    }

    return root.children ?? [];
  }

  /**
   * Processes a single file into the tree structure by creating the necessary folder hierarchy.
   * @param file The file to process into the tree
   * @param root The root node of the tree
   */
  processFileIntoTree(file: YamlConversionResultFile, root: FileNode): void {
    const pathParts = this.extractPathParts(file.FilePath);
    let currentNode = root;

    for (let i = 0; i < pathParts.length; i++) {
      const part = pathParts[i];
      const isFile = i === pathParts.length - 1;
      currentNode = this.findOrCreateChildNode(currentNode, part, isFile, file);
    }
  }

  /**
   * Extracts and normalizes path parts from a file path.
   * @param filePath The file path to process
   * @returns Array of path parts with empty segments filtered out
   */
  extractPathParts(filePath: string): string[] {
    return filePath.replace('./', '').split('/').filter(p => p);
  }

  /**
   * Finds an existing child node or creates a new one if it doesn't exist.
   * @param parentNode The parent node to search in
   * @param nodeName The name of the node to find or create
   * @param isFile Whether this node represents a file (vs folder)
   * @param file The source file data for content and notes
   * @returns The found or newly created child node
   */
  findOrCreateChildNode(
    parentNode: FileNode, 
    nodeName: string, 
    isFile: boolean, 
    file: YamlConversionResultFile
  ): FileNode {
    let childNode = parentNode.children?.find(c => c.name === nodeName);

    if (!childNode) {
      childNode = this.createFileNode(nodeName, isFile, file);
      parentNode.children ??= [];
      parentNode.children.push(childNode);
    }

    return childNode;
  }

  /**
   * Creates a new file node with appropriate properties based on type.
   * @param name The name of the node
   * @param isFile Whether this is a file node (vs folder)
   * @param file The source file data for content and notes
   * @returns A new FileNode with appropriate properties
   */
  private createFileNode(name: string, isFile: boolean, file: YamlConversionResultFile): FileNode {
    return {
      name,
      type: isFile ? 'file' : 'folder',
      children: isFile ? undefined : [],
      content: isFile ? file.Content : undefined,
      notes: isFile ? file.Notes : undefined,
    };
  }

  /**
   * Handles user selection of a file node in the tree structure.
   * Updates the selected file and displays its content in the preview area.
   * Only processes file nodes (not folder nodes) for content display.
   * Also loads and displays any associated notes for the selected file.
   * @param node The FileNode that was clicked by the user
   */
  selectFile(node: FileNode): void {
    if (node.type === 'file') {
      this.selectedFile = node;
      this.selectedFileNotes = node.notes ?? null;
    }
  }

  hasChild(_: number, node: FileNode): boolean {
    return !!node.children && node.children.length > 0;
  }

  childrenAccessor(node: FileNode): FileNode[] {
    return node.children ?? [];
  }

  // ===== 7. DIALOG AND UI METHODS =====

  /**
   * Opens the Request Changes dialog for users to provide feedback on the conversion results.
   * Displays a modal dialog where users can enter text feedback for refining the conversion.
   * If the user submits feedback, it triggers a refinement conversion with the provided prompt.
   * If the user cancels, no action is taken.
   */
  openRequestChangesDialog(): void {
    const dialogRef = this.dialog.open(RequestChangesDialogComponent, {
      width: '600px'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.convertToYaml(result);
      }
    });
  }

  /**
   * Resets the component to its initial state for starting a new conversion.
   * Clears all conversion-related data, selected files, results, and form values.
   * Returns the UI to the form state where users can select new pipelines to convert.
   * Called when user clicks "Start Over" or "Start New Conversion" buttons.
   */
  resetConversion(): void {
    this.conversionState = 'form';
    this.conversionError = null;
    this.selectedFile = null;
    this.selectedFileNotes = null;
    this.treeDataSource = [];
    this.conversionResult = [];
    this.migrationForm.reset({
      rcReleaseDefinition: '',
      devEnabled: false,
      devReleaseDefinition: ''
    });
  }

}
